import { Button } from "@mui/material";
import { makeStyles } from "@mui/styles";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";

import {
  isAteScoCompoundedOrderType,
  supportedUSStatesForCompoundedMeds,
} from "~/shared/utils/sharedUtils";
import { ProductImage } from "~/visits/components/payment/paymentComponents";
import { productName } from "~/visits/components/payment/paymentHelpers";
import {
  clearForceFlavoredBBs,
  clearVisitProduct,
  getBetaBlockerVariant,
  getOriginalRefillOrderItems,
  getVisitUSState,
  isFollowUpVisit,
  setFollowUpVisitFlag,
  setForceFlavoredBBs,
} from "~/visits/utils/visitCookies";
import imgFruitPunch from "~assets/images/flavors/tropical-fruit-medley-flavor.png";

import { kickBlue } from "~/shared/styles/baseStyles";

import {
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL,
} from "~/shared/sharedConstants";

const useStyles = makeStyles((theme) => ({
  content: {
    flexGrow: 1,
    padding: theme.spacing(1),
  },
}));

export default function RefillMakeChanges({ match }) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const consultationType = match.params.consultationType;
  const queryParams = new URLSearchParams(location.search);
  const checkoutSessionID = queryParams.get("checkoutSessionID");
  if (!checkoutSessionID) {
    window.location.href = "/me/portal";
  }

  const originalRefillOrderItems =
    getOriginalRefillOrderItems(checkoutSessionID);

  const handleMakeChanges = () => {
    clearForceFlavoredBBs(checkoutSessionID);
    clearVisitProduct(checkoutSessionID);
    setFollowUpVisitFlag("yes", checkoutSessionID);
    history.push(
      `/visits/${consultationType}/1?checkoutSessionID=${checkoutSessionID}`
    );
  };

  const handleNoChanges = () => {
    clearForceFlavoredBBs(checkoutSessionID);
    history.push(
      `/visits/${consultationType}/choose-refill-order-items?checkoutSessionID=${checkoutSessionID}`
    );
  };

  const handleTryFlavoredBBs = () => {
    setForceFlavoredBBs(true, checkoutSessionID);
    clearVisitProduct(checkoutSessionID);
    setFollowUpVisitFlag("yes", checkoutSessionID);
    history.push(
      `/visits/${consultationType}/1?checkoutSessionID=${checkoutSessionID}`
    );
  };

  // this is in case user starts to make changes and then goes back to the previous page
  if (isFollowUpVisit(checkoutSessionID)) {
    window.location.href = "/me/portal";
    return null;
  }

  const isRefillingFlavoredBBs = originalRefillOrderItems.some((orderItem) =>
    [
      ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL,
      ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL,
    ].includes(orderItem.productSlug)
  );
  const isRefillingAteSco =
    originalRefillOrderItems.some((orderItem) =>
      isAteScoCompoundedOrderType(orderItem.productSlug)
    ) ||
    consultationType ===
      CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP;

  const showFlavoredBBUpsell =
    supportedUSStatesForCompoundedMeds.includes(getVisitUSState()) &&
    !isRefillingFlavoredBBs &&
    !isRefillingAteSco;

  return (
    <main
      className={classes.content}
      style={{ backgroundColor: "#eee", minHeight: "100vh" }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "0 10px",
          maxWidth: "600px",
          margin: "0 auto",
        }}
      >
        <h3 style={{ textAlign: "center", marginBottom: "20px" }}>
          Your Kick Refill
        </h3>

        <div
          style={{
            padding: "20px",
            border: "1px solid #ddd",
            borderRadius: "6px",
            backgroundColor: "white",
            maxWidth: "600px",
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <h5 style={{ marginBottom: "10px" }}>
            Reorder the following product(s):
          </h5>
          {originalRefillOrderItems.map((orderItem) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                margin: "-8px 0",
              }}
              key={orderItem.productSlug}
            >
              <div style={{ width: "60px" }}>
                <ProductImage
                  classes={classes}
                  consultationType={orderItem.consultationType}
                  productType={orderItem.productSlug}
                  subcategory={orderItem.subcategory}
                  checkoutSessionID={checkoutSessionID}
                  imgStyle={{
                    height: "90px",
                    width: "90px",
                    objectFit: "scale-down",
                    paddingRight: "5px",
                  }}
                />
              </div>
              <div
                style={{
                  marginLeft: "20px",
                  fontSize: "16px",
                  lineHeight: "1.5",
                  padding: "0 18px",
                }}
              >
                {productName(
                  orderItem.productSlug,
                  orderItem.subcategory,
                  checkoutSessionID
                )}
              </div>
            </div>
          ))}
          <p
            style={{
              margin: "10px auto 4px",
              fontSize: "16px",
              marginTop: "10px",
            }}
          >
            {/* Complete a quick follow-up visit */}
          </p>
          <Button
            color="primary"
            size="large"
            style={{
              backgroundColor: kickBlue,
              color: "white",
              margin: "10px auto 10px",
              width: "300px",
            }}
            onClick={handleNoChanges}
          >
            Continue <NavigateNextIcon />
          </Button>
        </div>

        <h4
          style={{
            textAlign: "center",
            marginBottom: "15px",
            marginTop: "24px",
            fontSize: "20px",
          }}
        >
          Need to Make Medication Changes?
        </h4>

        <div
          style={{
            padding: "20px",
            border: "1px solid #ddd",
            borderRadius: "6px",
            backgroundColor: "white",
            maxWidth: "600px",
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {showFlavoredBBUpsell && (
            <div
              style={{
                marginBottom: "10px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <p
                style={{
                  fontSize: "20px",
                  fontFamily: "Spectral-Bold",
                  marginTop: "20px",
                  marginBottom: "16px",
                  alignSelf: "center",
                }}
              >
                - New 5x Faster & Stronger Mints -
              </p>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "24px",
                }}
              >
                <img
                  src={imgFruitPunch}
                  alt="Fruit Punch Flavored Troches"
                  style={{
                    width: "84px",
                    position: "relative",
                    marginBottom: "-10px",
                  }}
                />
                <h5 style={{ marginBottom: "0", marginLeft: "14px" }}>
                  Available in three flavors, our mints are 20% stronger and
                  start working in just 10 minutes
                </h5>
              </div>
              <Button
                variant="outlined"
                size="large"
                style={{
                  backgroundColor: kickBlue,
                  borderColor: kickBlue,
                  color: "white",
                  margin: "0 auto 10px",
                  width: "300px",
                }}
                onClick={handleTryFlavoredBBs}
              >
                Order Fast-Acting Mints <NavigateNextIcon />
              </Button>
              <hr
                style={{
                  width: "100%",
                  margin: "28px 0 0 0",
                  height: "1px",
                  backgroundColor: "#ddd",
                }}
              />
            </div>
          )}

          <h5 style={{ marginBottom: "0" }}>
            Complete a quick 5-minute follow-up to:
          </h5>
          <ul>
            <li
              style={{ paddingLeft: 0, fontSize: "16px", marginBottom: "4px" }}
            >
              Increase medication quantity
            </li>
            {["Propranolol 10mg", "Propranolol 12mg"].includes(
              getBetaBlockerVariant(checkoutSessionID)
            ) && (
              <li
                style={{
                  paddingLeft: 0,
                  fontSize: "16px",
                  marginBottom: "4px",
                }}
              >
                {isRefillingFlavoredBBs
                  ? "Increase dosage (12mg → 22mg)"
                  : "Increase dosage (10mg → 20mg)"}
              </li>
            )}
            <li
              style={{ paddingLeft: 0, fontSize: "16px", marginBottom: "4px" }}
            >
              Pickup from local pharmacy
            </li>
            {isRefillingFlavoredBBs && (
              <li
                style={{
                  paddingLeft: 0,
                  fontSize: "16px",
                  marginBottom: "4px",
                }}
              >
                Try another flavor
              </li>
            )}
          </ul>
          <p
            style={{
              margin: "10px auto 4px",
              fontSize: "16px",
              marginTop: "10px",
            }}
          >
            {/* Complete a quick follow-up visit */}
          </p>
          <Button
            variant="outlined"
            size="large"
            style={{
              borderColor: kickBlue,
              color: kickBlue,
              margin: "0 auto 10px",
              width: "300px",
            }}
            onClick={handleMakeChanges}
          >
            Quick Follow-up Visit <NavigateNextIcon />
          </Button>
        </div>
      </div>
    </main>
  );
}
